import { changeLanguage, mapSystemLanguageToSupported } from '../lib/i18n';
import {
  getCurrentEffectiveLanguage,
  setLanguagePreference,
  getUserLanguagePreference,
  hasUserLanguagePreference,
  getSystemLanguage
} from '../utils/languageUtils';
import { updateUserPreferencesAtom, userPreferencesAtom } from '../atoms/v3/userPreferencesAtoms';
import { getDefaultStore } from 'jotai';

/**
 * 统一的语言管理服务
 */
class LanguageManager {
  private systemLanguageWatcher: (() => void) | null = null;
  private store = getDefaultStore();

  /**
   * 初始化语言设置
   */
  async initialize(): Promise<void> {
    try {
      // 1. 检查用户是否已登录并有后端偏好
      const userPreferences = this.store.get(userPreferencesAtom);
      if (userPreferences?.preferredLanguage) {
        await this.applyLanguagePreference(userPreferences.preferredLanguage);
        return;
      }

      // 2. 检查本地用户偏好
      const localPreference = getUserLanguagePreference();
      if (localPreference) {
        await this.applyLanguagePreference(localPreference);
        return;
      }

      // 3. 如果没有任何偏好设置，使用系统语言作为默认
      const systemLanguage = getSystemLanguage();
      const effectiveLanguage = mapSystemLanguageToSupported(systemLanguage);
      await this.applyLanguagePreference('system');
      
      console.log('Language initialized with system default:', effectiveLanguage);
    } catch (error) {
      console.error('Failed to initialize language:', error);
      // 回退到中文
      await changeLanguage('zh');
    }
  }

  /**
   * 应用语言偏好
   */
  private async applyLanguagePreference(preference: string): Promise<void> {
    const effectiveLanguage = getCurrentEffectiveLanguage(preference);
    
    // 更新 i18next
    await changeLanguage(effectiveLanguage);
    
    // 更新本地存储
    setLanguagePreference(preference);
    
    // 如果是 system 选项，启动系统语言监听
    if (preference === 'system') {
      this.startSystemLanguageWatcher();
    } else {
      this.stopSystemLanguageWatcher();
    }
    
    console.log(`Language applied: ${preference} -> ${effectiveLanguage}`);
  }

  /**
   * 设置用户语言偏好
   */
  async setUserLanguagePreference(language: string): Promise<void> {
    try {
      // 1. 应用语言设置
      await this.applyLanguagePreference(language);
      
      // 2. 同步到后端（如果用户已登录）
      await this.syncToBackend(language);
      
      console.log('User language preference set:', language);
    } catch (error) {
      console.error('Failed to set user language preference:', error);
      throw error;
    }
  }

  /**
   * 同步语言偏好到后端
   */
  private async syncToBackend(language: string): Promise<void> {
    try {
      const updatePreferences = this.store.set(updateUserPreferencesAtom, { preferredLanguage: language });
      await updatePreferences;
      console.log('Language preference synced to backend:', language);
    } catch (error) {
      console.warn('Failed to sync language preference to backend:', error);
      // 不抛出错误，因为本地设置已经成功
    }
  }

  /**
   * 从后端同步语言偏好
   */
  async syncFromBackend(): Promise<void> {
    try {
      const userPreferences = this.store.get(userPreferencesAtom);
      if (userPreferences?.preferredLanguage) {
        const localPreference = getUserLanguagePreference();
        
        // 如果后端偏好与本地不同，使用后端偏好
        if (localPreference !== userPreferences.preferredLanguage) {
          await this.applyLanguagePreference(userPreferences.preferredLanguage);
          console.log('Language preference synced from backend:', userPreferences.preferredLanguage);
        }
      }
    } catch (error) {
      console.warn('Failed to sync language preference from backend:', error);
    }
  }

  /**
   * 启动系统语言监听器
   */
  private startSystemLanguageWatcher(): void {
    if (this.systemLanguageWatcher) {
      return; // 已经在监听
    }

    const handleLanguageChange = async () => {
      const userPreference = getUserLanguagePreference();
      if (userPreference === 'system') {
        const newSystemLanguage = getSystemLanguage();
        const newEffectiveLanguage = mapSystemLanguageToSupported(newSystemLanguage);
        await changeLanguage(newEffectiveLanguage);
        console.log('System language changed, updated to:', newEffectiveLanguage);
      }
    };

    // 监听语言变化事件
    window.addEventListener('languagechange', handleLanguageChange);
    
    this.systemLanguageWatcher = () => {
      window.removeEventListener('languagechange', handleLanguageChange);
    };
  }

  /**
   * 停止系统语言监听器
   */
  private stopSystemLanguageWatcher(): void {
    if (this.systemLanguageWatcher) {
      this.systemLanguageWatcher();
      this.systemLanguageWatcher = null;
    }
  }

  /**
   * 处理用户登录后的语言同步
   */
  async handleUserLogin(): Promise<void> {
    try {
      // 从后端获取用户偏好
      await this.syncFromBackend();
    } catch (error) {
      console.warn('Failed to handle user login language sync:', error);
    }
  }

  /**
   * 处理用户登出
   */
  handleUserLogout(): void {
    // 保持本地语言设置，不做任何改变
    console.log('User logged out, keeping local language preference');
  }

  /**
   * 获取当前语言偏好
   */
  getCurrentLanguagePreference(): string {
    return getUserLanguagePreference() || 'system';
  }

  /**
   * 清理资源
   */
  cleanup(): void {
    this.stopSystemLanguageWatcher();
  }
}

// 创建单例实例
export const languageManager = new LanguageManager();

// 导出类型
export type { LanguageManager };
